import { useCallback, useState } from "react";
import { Package, X } from "lucide-react";

const SCENE_OBJECT_IDS = Array.from({ length: 17 }, (_, i) => String(i + 1));

type SceneObjectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: { name: string; objectId: string }) => Promise<void>;
};

const SceneObjectModal: React.FC<SceneObjectModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [selectedObjectId, setSelectedObjectId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSelectedObjectId("");
    setName("");
    setError(null);
    setIsSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    resetState();
    onClose();
  }, [isSubmitting, onClose, resetState]);

  const handleConfirm = useCallback(async () => {
    if (!selectedObjectId) {
      setError("Selecione um objeto para adicionar ao cenário.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm({ name: name.trim(), objectId: selectedObjectId });
      resetState();
    } catch (err) {
      console.error("Erro ao confirmar objeto:", err);
      setError("Não foi possível adicionar o objeto. Tente novamente.");
      setIsSubmitting(false);
    }
  }, [name, onConfirm, resetState, selectedObjectId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-black/60">
      <div className="flex min-h-full items-center justify-center px-4 py-6">
        <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed"
            aria-label="Fechar"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex max-h-[calc(100vh-3rem)] flex-col overflow-y-auto">
            <div className="flex flex-col gap-6 p-6 sm:p-8">
              <header className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600/10 text-emerald-600">
                  <Package className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                    Adicionar objeto ao cenário
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Escolha um objeto para representar elementos da constelação. Apenas o moderador pode movê-lo ou removê-lo.
                  </p>
                </div>
              </header>

              <div className="grid gap-6 sm:grid-cols-[1.5fr_1fr]">
                {/* Grid de objetos */}
                <div>
                  <h3 className="mb-3 text-sm font-medium text-gray-700 uppercase tracking-wide">
                    Selecione um objeto
                  </h3>
                  <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
                    {SCENE_OBJECT_IDS.map((id) => (
                      <button
                        key={id}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => setSelectedObjectId(id)}
                        className={`relative aspect-square rounded-xl border-2 p-1.5 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                          selectedObjectId === id
                            ? "border-emerald-500 bg-emerald-50 shadow-md"
                            : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50"
                        } ${isSubmitting ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <img
                          src={`/assets/objects/${id}.png`}
                          alt={`Objeto ${id}`}
                          className="h-full w-full object-contain"
                          draggable={false}
                        />
                        {selectedObjectId === id && (
                          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-bold text-white">
                            ✓
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Painel direito */}
                <div className="rounded-xl bg-gray-50 p-4">
                  <div className="space-y-4">
                    {/* Preview */}
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex h-24 w-24 items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-white">
                        {selectedObjectId ? (
                          <img
                            src={`/assets/objects/${selectedObjectId}.png`}
                            alt="Preview"
                            className="h-full w-full object-contain p-2"
                            draggable={false}
                          />
                        ) : (
                          <Package className="h-10 w-10 text-gray-300" />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {selectedObjectId ? `Objeto ${selectedObjectId}` : "Nenhum selecionado"}
                      </span>
                    </div>

                    {/* Campo de nome */}
                    <div>
                      <label className="text-sm font-medium text-gray-700" htmlFor="scene-object-name">
                        Rótulo (opcional)
                      </label>
                      <input
                        id="scene-object-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex.: Ancestral, Trauma, Casa"
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        maxLength={60}
                        disabled={isSubmitting}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Aparece abaixo do objeto no cenário.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <span>{error}</span>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Processando...
                    </span>
                  ) : (
                    <span>Adicionar</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SceneObjectModal;
